import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { formatMoney, formatUK } from "@/lib/format";
import { Search, Loader2, Copy, Eye } from "lucide-react";
import { toast } from "sonner";

const statusBadge = {
  draft: "bg-slate-100 text-slate-700", finalised: "bg-emerald-100 text-emerald-700",
  superseded: "bg-amber-100 text-amber-700", cancelled: "bg-red-100 text-red-700",
};

export default function DividendHistory() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [rows, setRows] = useState(null);
  const [companyId, setCompanyId] = useState("all");
  const [status, setStatus] = useState("all");
  const [year, setYear] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)); }, []);

  const load = () => {
    const params = {};
    if (companyId !== "all") params.company_id = companyId;
    if (status !== "all") params.status = status;
    if (year !== "all") params.year = year;
    if (q) params.q = q;
    api.get("/dividends", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [companyId, status, year, q]);

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  const duplicate = async (e, id) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/dividends/${id}/duplicate`);
      toast.success("Duplicated to next period (Draft)");
      navigate(`/dividends/${res.data.id}`);
    } catch (err) { toast.error("Duplicate failed"); }
  };

  const years = ["all", ...Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i)];

  return (
    <div data-testid="history-page">
      <PageHeader title="Dividend History" description="View, download and duplicate past dividend records." />

      <Card className="mb-6 border-cass-border p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cass-navy/40" />
            <Input placeholder="Search shareholder, period, status" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="history-search" />
          </div>
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger data-testid="history-company"><SelectValue placeholder="Company" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">All companies</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="history-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All statuses</SelectItem><SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="finalised">Finalised</SelectItem><SelectItem value="superseded">Superseded</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(v)}>
            <SelectTrigger data-testid="history-year"><SelectValue placeholder="Year" /></SelectTrigger>
            <SelectContent className="bg-white">{years.map((y) => <SelectItem key={y} value={String(y)}>{y === "all" ? "All years" : y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-cass-border shadow-sm">
        {rows === null ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cass-blue" /></div> : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-cass-navy/50">No dividend records found.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-cass-surface hover:bg-cass-surface">
                <TableHead className="text-xs uppercase tracking-wider">Company</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Period</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Payment date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Class</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider">Holders</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider">Total</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((d) => (
                <TableRow key={d.id} className="cursor-pointer hover:bg-cass-surface" onClick={() => navigate(`/dividends/${d.id}`)} data-testid={`history-row-${d.id}`}>
                  <TableCell className="font-medium text-cass-navy">{companyName(d.company_id)}</TableCell>
                  <TableCell className="text-cass-navy/70">{d.period_label || "—"}</TableCell>
                  <TableCell className="text-cass-navy/70 tabular">{formatUK(d.payment_date)}</TableCell>
                  <TableCell className="text-cass-navy/70">{d.share_class}</TableCell>
                  <TableCell className="text-right text-cass-navy/70 tabular">{(d.lines || []).length}</TableCell>
                  <TableCell className="text-right font-semibold text-cass-navy tabular">{formatMoney(d.grand_total)}</TableCell>
                  <TableCell><Badge className={`${statusBadge[d.status]} border-0 capitalize`}>{d.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/dividends/${d.id}`); }}><Eye className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={(e) => duplicate(e, d.id)} data-testid={`duplicate-${d.id}`}><Copy className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
